/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { logger } from '@/services/logger'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: {
      title: 'Home',
    },
  },
  {
    path: '/editor',
    name: 'Editor',
    component: () => import('@/views/Editor.vue'),
    meta: {
      title: 'Editor',
      requiresAuth: true,
    },
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import('@/views/Pricing.vue'),
    meta: {
      title: 'Pricing',
    },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/views/Settings.vue'),
    meta: {
      title: 'Settings',
      requiresAuth: true,
    },
  },
  {
    path: '/support',
    name: 'Support',
    component: () => import('@/views/Support.vue'),
    meta: {
      title: 'Support',
    },
  },
  {
    path: '/grammar-checker',
    name: 'GrammarChecker',
    component: () => import('@/views/GrammarChecker.vue'),
    meta: {
      title: 'Grammar Checker',
    },
  },
  {
    path: '/ai-writing-assistant',
    name: 'WritingAssistant',
    component: () => import('@/views/WritingAssistant.vue'),
    meta: {
      title: 'AI Writing Assistant',
    },
  },
  {
    path: '/spell-checker',
    name: 'SpellChecker',
    component: () => import('@/views/SpellChecker.vue'),
    meta: {
      title: 'Spell Checker',
    },
  },
  {
    path: '/plagiarism-checker',
    name: 'PlagiarismChecker',
    component: () => import('@/views/PlagiarismChecker.vue'),
    meta: {
      title: 'Plagiarism Checker',
    },
  },
  {
    path: '/ai-content-detector',
    name: 'AIContentDetector',
    component: () => import('@/views/AIContentDetector.vue'),
    meta: {
      title: 'AI Content Detector',
    },
  },
  {
    path: '/citation-checker',
    name: 'CitationChecker',
    component: () => import('@/views/CitationChecker.vue'),
    meta: {
      title: 'Citation Checker',
    },
  },
  {
    path: '/extension',
    name: 'Extension',
    component: () => import('@/views/Extension.vue'),
    meta: {
      title: 'Chrome Extension',
    },
  },

  {
    path: '/subscribe',
    name: 'Subscribe',
    component: () => import('@/views/Subscribe.vue'),
    meta: {
      title: 'Subscribe',
      requiresAuth: true,
    },
  },
  {
    path: '/checkout/success',
    name: 'CheckoutSuccess',
    component: () => import('@/views/checkout/Success.vue'),
    meta: {
      title: 'Subscription Successful',
      requiresAuth: true,
    },
  },
  {
    path: '/checkout/cancel',
    name: 'CheckoutCancel',
    component: () => import('@/views/checkout/Cancel.vue'),
    meta: {
      title: 'Subscription Cancelled',
      requiresAuth: true,
    },
  },
  {
    path: '/auth/signin',
    name: 'SignIn',
    component: () => import('@/views/auth/SignIn.vue'),
    meta: {
      title: 'Sign In',
      hideForAuth: true,
    },
  },
  {
    path: '/auth/signup',
    name: 'SignUp',
    component: () => import('@/views/auth/SignUp.vue'),
    meta: {
      title: 'Sign Up',
      hideForAuth: true,
    },
  },
  {
    path: '/auth/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPassword.vue'),
    meta: {
      title: 'Forgot Password',
      hideForAuth: true,
    },
  },
  {
    path: '/auth/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/auth/ResetPassword.vue'),
    meta: {
      title: 'Reset Password',
      hideForAuth: true,
    },
  },
  {
    path: '/auth/verify-email',
    name: 'VerifyEmail',
    component: () => import('@/views/auth/VerifyEmail.vue'),
    meta: {
      title: 'Verify Email',
      hideForAuth: true,
    },
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/PrivacyPolicy.vue'),
    meta: {
      title: 'Privacy Policy',
    },
  },
  {
    path: '/error',
    name: 'Error',
    component: () => import('@/views/Error.vue'),
    meta: {
      title: 'Error',
    },
  },

  // Language-specific routes for SEO and hreflang support
  {
    path: '/en',
    name: 'EnglishHome',
    component: () => import('@/views/Home.vue'),
    beforeEnter: async (_to, _from, next) => {
      // Import setLocale function and set English locale
      const { setLocale } = await import('@/i18n')
      setLocale('en')
      next()
    },
    meta: {
      title: 'Home',
      locale: 'en',
    },
  },
  {
    path: '/fr',
    name: 'FrenchHome',
    component: () => import('@/views/Home.vue'),
    beforeEnter: async (_to, _from, next) => {
      // Import setLocale function and set French locale
      const { setLocale } = await import('@/i18n')
      setLocale('fr')
      next()
    },
    meta: {
      title: 'Accueil',
      locale: 'fr',
    },
  },
  {
    path: '/es',
    name: 'SpanishHome',
    component: () => import('@/views/Home.vue'),
    beforeEnter: async (_to, _from, next) => {
      // Import setLocale function and set Spanish locale
      const { setLocale } = await import('@/i18n')
      setLocale('es')
      next()
    },
    meta: {
      title: 'Inicio',
      locale: 'es',
    },
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: 'Page Not Found',
    },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    // If there's a saved position (e.g., user used browser back button), restore it
    if (savedPosition) {
      return savedPosition
    }
    // If the destination route has a hash, scroll to that element
    else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }
    // Otherwise, scroll to the top of the page
    else {
      return { top: 0 }
    }
  },
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  // Update document title
  if (to.meta.title) {
    document.title = `${to.meta.title} - n.writer`
  }

  // Import stores dynamically to avoid circular dependency
  const { useAuthStore } = await import('@/stores/auth')
  const { useSubscriptionStore } = await import('@/stores/subscription')
  const authStore = useAuthStore()
  const subscriptionStore = useSubscriptionStore()

  // Handle token-based authentication from extension
  if (to.query.token && typeof to.query.token === 'string') {
    try {
      // Authenticate using the provided token
      const success = await authStore.authenticateWithToken(to.query.token)

      // Remove token from URL for security and clean navigation
      const cleanQuery = { ...to.query }
      delete cleanQuery.token

      if (success) {
        // Authentication successful, redirect to the same route without the token parameter
        next({
          path: to.path,
          query: Object.keys(cleanQuery).length > 0 ? cleanQuery : undefined,
          replace: true,
        })
        return
      } else {
        // Authentication failed, redirect to sign-in with helpful message
        logger.warn(
          'Token authentication failed, redirecting to sign-in',
          { token: String(to.query.token).substring(0, 10) + '...' },
          'router'
        )
        next({
          path: '/auth/signin',
          query: {
            ...cleanQuery,
            redirect: encodeURIComponent(to.fullPath.split('?')[0]), // Remove token from redirect URL
            error: 'token_expired',
          },
          replace: true,
        })
        return
      }
    } catch (error) {
      logger.error(
        'Token authentication error:',
        error instanceof Error ? error : undefined,
        { token: String(to.query.token).substring(0, 10) + '...' },
        'router'
      )

      // Remove token from URL and redirect to sign-in with error
      const cleanQuery = { ...to.query }
      delete cleanQuery.token

      next({
        path: '/auth/signin',
        query: {
          ...cleanQuery,
          redirect: encodeURIComponent(to.fullPath.split('?')[0]),
          error: 'auth_error',
        },
        replace: true,
      })
      return
    }
  }

  // Redirect authenticated users away from auth pages
  if (to.meta.hideForAuth && authStore.isAuthenticated) {
    next('/')
    return
  }

  // Redirect unauthenticated users from protected pages
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next(`/auth/signin?redirect=${encodeURIComponent(to.fullPath)}`)
    return
  }

  // Optimize workflow for premium users: redirect from home to editor
  if (to.path === '/' && authStore.isAuthenticated && subscriptionStore.isPremium) {
    // Allow explicit access to home page with query parameter
    if (!to.query.showHome) {
      // Preserve any existing query parameters for analytics
      const query = { ...to.query }
      next({ path: '/editor', query })
      return
    }
  }

  // For authenticated users on home page, refresh user data and subscription data
  if (to.path === '/' && authStore.isAuthenticated && !authStore.isTokenExpired) {
    try {
      // Refresh user data to get current usage count and other updated info
      await authStore.getCurrentUser()

      // Initialize subscription data for all authenticated users
      if (authStore.user?.id) {
        await subscriptionStore.initializeSubscription()
      }
    } catch (error) {
      logger.warn(
        'Failed to refresh user or subscription data:',
        { error: error instanceof Error ? error.message : String(error) },
        'router'
      )
      // Continue navigation even if data refresh fails
    }
  }

  next()
})

export default router
